/** Types for the Safety System (V1 Safety port) — Hub dashboard + Units grid + Unit drawer. */

/**
 * Units grid views (units_query.VIEWS). `driver` / `truck` = one row per unit with the V1 layouts;
 * `payroll` = one row per deduction / insurance child (`rowid` = `source:childrecno`); `cert` = one row
 * per license / permit / training (`rowid` = `unitrecno:lictype:childrecno`); `retention` / `loadrequest`
 * = one row per unit.
 */
export type UnitsView = 'driver' | 'truck' | 'payroll' | 'cert' | 'retention' | 'loadrequest';

export interface DriverSummary {
  active: number;
  inservice: number;
  outofservice: number;
  inservice_pct: number;
  outofservice_pct: number;
  muledriver: number;
  oos_by_type: { type: string; count: number }[];
  dispatched: number;
  dispatched_local: number;
  dispatched_otr: number;
  notdispatched: number;
  notdispatched_local: number;
  notdispatched_otr: number;
  notworking: number;
  unknown: number;
}

/** Each bucket is null when its query failed server-side (the hub degrades per bucket). */
export interface RecordsBuckets {
  open_tickets: number | null;
  open_maint_reports: number | null;
  open_incidents: number | null;
  driverless_trucks: number | null;
  truckless_mule_drivers: number | null;
  truckless_w2_temp_drivers: number | null;
  hazmat_pros: number | null;
}

export interface ExpirationItem {
  date: string;
  field: string;
  label: string;
  units: string[];
}

export interface Expirations {
  today: string | null;
  past_due: ExpirationItem[];
  upcoming: ExpirationItem[];
  empty: { field: string; units: string[] }[];
}

export interface SafetyRegion {
  recno: number;
  label: string;
}

export interface UnitRow {
  unitrecno: number | null;
  unitnumber: string;
  driverrecno: number | null;
  truckrecno: number | null;
  /** `unit.ownerrecno` (both views) — the incident Equipment tab's unit typeahead fills `equipment.ownerrecno` from it. */
  ownerrecno?: number | null;
  /** Truck view only. */
  truckownername?: string | null;
  drivername: string;
  status: string;
  oosdays: number;
  outofservicetype: string | null;
  region: string | null;
  /** Payroll / cert views: the child-row identity (TanTable row id); retention / loadrequest echo `unitrecno`. */
  rowid?: string | number | null;
  [k: string]: unknown;
}

/** One row of GET /safety-system/documents (safetynew_map + fileinfo). */
export interface SafetyDocument {
  recno: number;
  fileinforecno: number;
  documenttype: string;
  description: string;
  filename: string;
  filetype: string;
  directory: string;
  uploaddatetime: string | null;
  uploadedby: string | null;
  url: string;
}

/** One row of GET /safety-system/notes (newest first). */
export interface SafetyNote {
  recno: number;
  datetime: string;
  userid: string;
  notes: string;
}

/** Tables the Documents tab can attach files to (documents.py ALLOWED_TABLENAMES). */
export type SafetyDocumentTable =
  | 'driver' | 'truck' | 'unit' | 'ticket' | 'incident' | 'incident_equipment' | 'maintrepair' | 'inspectiondefect' | 'owner';

/** GET /safety-system/units/{recno}. Header photos are NOT here: the drawer reads them from GET /documents. */
export interface UnitDetail {
  unit: Record<string, any>;
  driver: Record<string, any>;
  truck: Record<string, any>;
  owner: Record<string, any>;
  lookups: Record<string, { recno: number; label: string }[]>;
}

export interface UnitsListParams {
  view: UnitsView;
  status?: string;
  region?: string;
  include_mules?: boolean;
  q?: string;
  search_by?: string;
  bucket?: string;
}

export interface UnitsListResponse {
  data: UnitRow[];
  count: number;
  view: UnitsView;
  /** `loadrequest` only: set when the cms settlement totals failed (the four money columns are null). */
  settlements_error?: string;
}

// ---------------------------------------------------------------------------
// Phase 2: Tickets / Maintenance reports / Incidents
// ---------------------------------------------------------------------------

/** `ticket` = one row per ticket (identity `ticketrecno`); `tv` = one row per violation (identity `tvrecno`). */
export type TicketsView = 'ticket' | 'tv';
export type TicketSearchBy = 'ticketnum' | 'unitnumber' | 'drivername' | 'incidentrecno';

export interface TicketsListParams {
  view: TicketsView;
  open?: boolean;
  q?: string;
  search_by?: TicketSearchBy;
  include_deleted?: boolean;
}

/** Ticket view row (tickets_query._TICKET_COLS). `ticketoos` is a 0/1 EXISTS flag. */
export interface TicketRow {
  ticketrecno: number;
  ticketdeleted: string;
  driverrecno: number | null;
  incidentrecno: number | null;
  ticketnum: string;
  tdrivername: string | null;
  unitnumber: string | null;
  pronumber: number | string | null;
  statecopyrcvddate: string | null;
  physcopyrcvddate: string | null;
  ticketsentdate: string | null;
  totticketweight: number | null;
  inspectedbyname: string | null;
  ticketprecinct: string | null;
  ticketcounty: string | null;
  tickethwy: string | null;
  milemarker: string | null;
  ticketstartdate: string | null;
  ticketstarttime: string | null;
  ticketendtime: string | null;
  ticketclean: string | null;
  ticketlevel: string | null;
  closedatetime: string | null;
  closedby: string | null;
  createdby: string | null;
  createdatetime: string | null;
  ticketoos: number | boolean | null;
  region: string | null;
  preference: string | null;
  companyrep: string | null;
  [k: string]: unknown;
}

/** Violations view row (tickets_query._TV_COLS): the violation plus the equipment row it points at. */
export interface TicketViolationRow {
  tvrecno: number;
  ticketrecno: number;
  ticketnum: string;
  tdrivername: string | null;
  pronumber: number | string | null;
  wasticketed: string | null;
  violation: string | null;
  violationdesc: string | null;
  violationunit: string | null;
  violationoos: string | null;
  severityweight: number | null;
  timeweight: number | null;
  totalweight: number | null;
  ticketequipunit: string | null;
  ticketequiptype: string | null;
  ticketequipmake: string | null;
  ticketequipco: string | null;
  ticketequipplate: string | null;
  ticketequipstate: string | null;
  ticketequipvin: string | null;
  ticketequipowner: string | null;
  [k: string]: unknown;
}

export interface TicketsListResponse {
  data: (TicketRow | TicketViolationRow)[];
  count: number;
  view: TicketsView;
}

/** Editable `ticket_violation` child row. `recno` absent = new; `deleted:'Y'` soft-deletes an existing row. */
export interface TicketViolation {
  recno?: number;
  wasticketed?: string | null;
  violation?: string | null;
  violationdesc?: string | null;
  violationunit?: string | null;
  violationoos?: string | null;
  severityweight?: number | string | null;
  timeweight?: number | string | null;
  totalweight?: number | string | null;
  deleted?: string;
}

/** Editable `ticket_equipment` child row. */
export interface TicketEquipment {
  recno?: number;
  ticketequipunit?: string | null;
  ticketequiptype?: string | null;
  ticketequipmake?: string | null;
  ticketequipco?: string | null;
  ticketequipplate?: string | null;
  ticketequipstate?: string | null;
  ticketequipvin?: string | null;
  ticketequipowner?: string | null;
  deleted?: string;
}

export interface TicketOfficer {
  recno: number;
  label: string;
  badgenum?: string | null;
}

/** GET /safety-system/tickets/lookups (also embedded in the drawer load). */
export interface TicketLookups {
  officers: TicketOfficer[];
  equipowners: string[];
}

/** GET /safety-system/tickets/{recno}. */
export interface TicketDetail {
  ticket: Record<string, any>;
  violations: TicketViolation[];
  equipment: TicketEquipment[];
  lookups: TicketLookups;
}

/** One row of GET /safety-system/tickets/lookups/violations?q= (weights pre-computed: time=3, total=severity*3). */
export interface ViolationLookupRow {
  violation: string;
  violationdesc: string | null;
  weight: number | null;
  csabasics: string | null;
  groupdesc: string | null;
  severityweight: number;
  timeweight: number;
  totalweight: number;
}

/** POST /safety-system/tickets and PUT /safety-system/tickets/{recno}. `ticket.closed` closes / reopens. */
export interface TicketSaveBody {
  ticket: Record<string, any> & { closed?: boolean };
  violations?: TicketViolation[];
  equipment?: TicketEquipment[];
  bypass_dupe?: boolean;
}

export interface TicketDupe {
  recno: number;
  ticketnum: string;
  tdrivername: string | null;
}

export interface TicketSaveResponse {
  success: boolean;
  recno: number;
  created: boolean;
  changed: string[];
  children: Record<string, unknown>;
  totticketweight: number;
  warnings: string[];
}

/** One row of the `*_history` readers (ticket / maintenancereport / incident / unit). */
export interface SafetyHistoryRow {
  recno: number;
  /**
   * Which history table the row came from. Only the unit reader sets it: V1 keeps
   * `unit_history` / `driver_history` / `truck_history` and merges them for the drawer.
   */
  source?: 'unit' | 'driver' | 'truck' | string;
  dbtable: string;
  keyvalue: string;
  datetime: string;
  userid: string;
  colname: string;
  oldval: string | null;
  newval: string | null;
  type: string;
  otherid: string | null;
}

export interface SafetyHistoryResponse {
  data: SafetyHistoryRow[];
  count: number;
}

// ---- Maintenance reports -------------------------------------------------

export interface MaintenanceListParams {
  open?: boolean;
  status?: string;
  unitnumber?: string;
  year?: string | number;
  month?: string | number;
  include_deleted?: boolean;
}

export interface MaintenanceReportRow {
  /** List rows carry `maintreportrecno` (maintenance_query.py); the detail `report` carries `recno`. */
  maintreportrecno?: number;
  recno?: number;
  unitnumber: string | null;
  driverrecno: number | null;
  truckrecno: number | null;
  maintstatus: string | null;
  maintmonth: number | string | null;
  maintyear: number | string | null;
  maintreportdate: string | null;
  maintsubmitdate: string | null;
  maintapprovedby: string | null;
  maintapprovedatetime: string | null;
  truckmileage: string | number | null;
  maintfined: string | null;
  deleted?: string;
  [k: string]: unknown;
}

export interface MaintenanceListResponse {
  data: MaintenanceReportRow[];
  count: number;
}

export interface MaintenanceDefect {
  defectrecno: number;
  name: string;
  maintsection: string;
  defectdetails: string | null;
  inspectiondefectrecno: number | null;
  isdefect: 'Y' | 'N' | string;
  defectcomments: string;
}

export interface MaintenanceDefectSection {
  maintsection: string;
  defects: MaintenanceDefect[];
}

/** GET /safety-system/maintenance-reports/{recno}. */
export interface MaintenanceDetail {
  report: Record<string, any>;
  tickets_for_month: Record<string, any>[];
  repairs: { recno: number; repairdate: string | null; repaircomments: string | null }[];
  defects: MaintenanceDefectSection[];
  corrections: SafetyNote[];
}

/**
 * POST / PUT /safety-system/maintenance-reports. `report` carries the REPORT_FIELDS diff plus
 * the controls `approve: true`, `corrections_needed: { reason }`, `deleted: 'Y'` + `delete_reason`.
 */
export interface MaintenanceSaveBody {
  report: Record<string, any> & {
    approve?: boolean;
    corrections_needed?: { reason: string };
    deleted?: string;
    delete_reason?: string;
  };
}

export interface MaintenanceSaveResponse {
  success: boolean;
  recno: number;
  created: boolean;
  changed: string[];
  status: string;
  seeded_defects: number;
  email: Record<string, unknown> | null;
}

export interface DefectSaveRow {
  defectrecno: number;
  isdefect?: 'Y' | 'N';
  defectcomments?: string;
}

export interface DefectsSaveResponse {
  success: boolean;
  recno: number;
  updated: number[];
  inserted: number[];
  numdefects: number;
}

// ---- Incidents -------------------------------------------------------------

export type IncidentsView = 'incident' | 'invparty' | 'equipcargo' | 'claim' | 'cost';
export type IncidentSearchBy = 'incidentrecno' | 'gwiclaimnum' | 'unitnumber' | 'drivername' | 'claimnumber' | 'pronumber';

export interface IncidentsListParams {
  view: IncidentsView;
  open?: boolean;
  q?: string;
  search_by?: IncidentSearchBy;
  include_deleted?: boolean;
}

/** Common columns of every incidents view; the per-view extras are open-keyed. */
export interface IncidentRow {
  incidentrecno: number;
  gwiclaimnum: string | null;
  incidentstatus: string | null;
  incidenttype: string | null;
  incidentdatetime: string | null;
  incidentreportedby: string | null;
  incidentdept: string | null;
  policenotified: string | null;
  statementrcvd: string | null;
  incidentdescription: string | null;
  incidentlocation: string | null;
  incidentrootcause: string | null;
  incidentclosedate: string | null;
  incidentsubmitdate: string | null;
  preventable: string | null;
  totalclaimreserves: number | string | null;
  accountability_group: string | null;
  pronumber: number | string | null;
  incidentdeleted: string | null;
  incidentinj: number | boolean | null;
  incidentfat: number | boolean | null;
  incidenttow: number | boolean | null;
  drivername: string | null;
  [k: string]: unknown;
}

export interface IncidentsListResponse {
  data: IncidentRow[];
  count: number;
  view: IncidentsView;
}

export interface IncidentSummary {
  costs: number;
  reimbursements: number;
  chargeback: number;
  out_of_pocket: number;
  injuries: number;
  fatalities: number;
  towed: number;
}

/** `{recno, label}` pick-list row (incidents.py `_LOOKUP_SQL`). */
export interface LookupOption {
  recno: number;
  label: string;
}

/** `departments` carries the department email too (testgwi.departments). */
export interface DepartmentLookup extends LookupOption {
  email: string | null;
}

/** `states`: the two-letter code is the key (`state AS recno`). */
export interface StateLookup {
  recno: string;
  label: string;
}

/** `claimtypes`: the claim type with its policy chain (V1 claimtypechange auto-fill). */
export interface ClaimTypeLookup {
  recno: number;
  claimtype: string;
  gwideductible?: unknown;
  driverdeductible?: unknown;
  policyrecno?: number | null;
  policynum?: string | null;
  policyholderrecno?: number | null;
  policyholdername?: string | null;
  policytyperecno?: number | null;
  policytypename?: string | null;
}

/** `IncidentDetail.lookups` — one key per incidents.py `_LOOKUP_SQL` entry. */
export interface IncidentLookups {
  incidenttypes: LookupOption[];
  rootcauses: LookupOption[];
  departments: DepartmentLookup[];
  accountability_groups: LookupOption[];
  claimtypes: ClaimTypeLookup[];
  policytypes: LookupOption[];
  policyholders: LookupOption[];
  costtypes: LookupOption[];
  paymenttypes: LookupOption[];
  paidto: LookupOption[];
  states: StateLookup[];
}

export const emptyIncidentLookups = (): IncidentLookups => ({
  incidenttypes: [], rootcauses: [], departments: [], accountability_groups: [], claimtypes: [], policytypes: [], policyholders: [],
  costtypes: [], paymenttypes: [], paidto: [], states: [],
});

/** GET /safety-system/incidents/{recno}. */
export interface IncidentDetail {
  incident: Record<string, any>;
  parties: Record<string, any>[];
  equipment: Record<string, any>[];
  claims: Record<string, any>[];
  costs: Record<string, any>[];
  costlines: Record<string, any>[];
  tickets: Record<string, any>[];
  summary: IncidentSummary;
  lookups: IncidentLookups;
}

/** POST / PUT /safety-system/incidents. Children follow the ticket child-row convention. */
export interface IncidentSaveBody {
  incident: Record<string, any>;
  parties?: Record<string, any>[];
  equipment?: Record<string, any>[];
  claims?: Record<string, any>[];
  costs?: Record<string, any>[];
  deletecomments?: string | null;
  noshow?: { drug?: string; alcohol?: string; alcohollate?: string };
}

export interface IncidentSaveResponse {
  success: boolean;
  recno: number;
  created: boolean;
  changed: string[];
  status: string;
  children: Record<string, unknown>;
  warnings: string[];
  emails: Record<string, unknown>;
}

/** PUT /safety-system/incidents/{recno}/costs/{acrecno}/lines. */
export interface CostLineRow {
  recno?: number;
  claimrecno: number;
  cost: number | string;
  glcode: string;
  deleted?: string;
}

export interface CostLinesSaveResponse {
  success: boolean;
  recno: number;
  acrecno: number;
  lines: Record<string, unknown>;
  totalaccost: number;
}

export interface TicketLinkResponse {
  success: boolean;
  recno: number;
  ticketrecno: number;
  ticketnum: string;
  changed: boolean;
}

// ---------------------------------------------------------------------------
// Phase 3: Unit drawer child rows (unit_children.py) + Payroll tab (payroll.py)
// ---------------------------------------------------------------------------

/** The nine child lists of `GET/PUT /safety-system/units/{recno}/children`. */
export type UnitChildKey =
  | 'restrictions' | 'permits' | 'owpermits' | 'training' | 'eld' | 'insurance' | 'deductions' | 'surveys' | 'awards';

export interface RestrictionTypeLookup {
  recno: number;
  cardtype: string;
  restrictioncode: string;
  type: string;
}

export interface DeductionTypeLookup extends LookupOption {
  defaultrate: unknown;
}

/** `UnitChildren.lookups` — one key per unit_children.py LOOKUP_SQL / FIXED_LOOKUPS entry. */
export interface UnitChildLookups {
  restrictiontypes: RestrictionTypeLookup[];
  permittypes: LookupOption[];
  owpermittypes: LookupOption[];
  trainingsubjects: LookupOption[];
  deductiontypes: DeductionTypeLookup[];
  surveytypes: LookupOption[];
  awardtypes: LookupOption[];
  countries: LookupOption[];
  trainingtypes: string[];
  eldtypes: string[];
  instypes: string[];
  payrollgroups: string[];
  cardtypes: string[];
}

export const emptyUnitChildLookups = (): UnitChildLookups => ({
  restrictiontypes: [], permittypes: [], owpermittypes: [], trainingsubjects: [], deductiontypes: [], surveytypes: [],
  awardtypes: [], countries: [], trainingtypes: [], eldtypes: [], instypes: [], payrollgroups: [], cardtypes: [],
});

/**
 * GET /safety-system/units/{recno}/children. `unit` = the parent recnos (+ truck cmv);
 * `driver` = the Cert / Retention scalar columns (not part of GET /units/{recno}).
 */
export interface UnitChildren extends Record<UnitChildKey, Record<string, any>[]> {
  unit: { unitrecno: number; unitnumber: string; driverrecno: number; truckrecno: number; cmv: unknown };
  driver: Record<string, any>;
  lookups: UnitChildLookups;
}

/** PUT /safety-system/units/{recno}/children — only the touched lists are present. */
export type UnitChildrenSaveBody = Partial<Record<UnitChildKey, Record<string, any>[]>>;

export interface UnitChildrenSaveResponse {
  success: boolean;
  unitrecno: number;
  children: Partial<Record<UnitChildKey, { inserted: number[]; updated: number[]; deleted: number[] }>>;
}

/** One entry of the structured 422 `{message, errors:[…]}` (children / cert / retention / payroll). */
export interface UnitFieldError {
  key: string;
  index?: number | null;
  field: string;
  message: string;
}

/** PUT /units/{recno}/cert and /retention: `{driver: {changed columns}}`. */
export interface UnitDriverFieldsSaveResponse {
  success: boolean;
  unitrecno: number;
  changed: { driver: string[] };
}

/** `sensitive.mask_bank`: presence + last four digits, never the number. */
export interface MaskedBankNumber {
  on_file: boolean;
  last4: string | null;
}

export interface UnitDirectDeposit {
  bankname1: unknown; accounttype1: unknown; nameonaccount1: unknown; amount1: unknown; hold1: unknown;
  routing1: MaskedBankNumber; account1: MaskedBankNumber;
  bankname2: unknown; accounttype2: unknown; nameonaccount2: unknown; amount2: unknown; hold2: unknown;
  routing2: MaskedBankNumber; account2: MaskedBankNumber;
}

export interface UnitInsuranceCurrent {
  instype: string;
  insamt: unknown;
  insstartdate: unknown;
  insenddate: unknown;
  insinceptiondate: unknown;
}

export interface UnitSettlements {
  loans: number | null;
  deferments: number | null;
  escrow: number | null;
  additional: number | null;
  difference: number | null;
}

/** GET /safety-system/units/{recno}/payroll. */
export interface UnitPayroll {
  unit: {
    startdate: unknown; payrollhourly: unknown; payrolldisclosure: unknown; payrollgroup: unknown;
    insbobtailgwi: unknown; inscompcollisiongwi: unknown; insoccupationalgwi: unknown; insliabilitygwi: unknown;
  };
  insurance_current: UnitInsuranceCurrent[];
  settlements: UnitSettlements;
  settlements_error?: string;
  direct_deposit: UnitDirectDeposit;
}

/** POST /safety-system/units/{recno}/payroll/reveal — plaintext, shown only until the tab closes. */
export interface UnitPayrollReveal {
  routing1: string;
  account1: string;
  routing2: string;
  account2: string;
}

export interface UnitPayrollSaveResponse {
  success: boolean;
  unitrecno: number;
  changed: { unit: string[]; sensitive: string[] };
}

// ---------------------------------------------------------------------------
// Report Center (Phase 4) — GET /safety-system/reports[/{id}[/xlsx]]
// ---------------------------------------------------------------------------

export type SafetyReportParamKind = 'date' | 'datetime' | 'select' | 'text' | 'textarea';

/** One parameter definition from the catalog. Values travel as strings:
 *  `date` = `YYYY-MM-DD`, `datetime` = `YYYY-MM-DDTHH:MM`, `select` = option value, `text` = raw,
 *  `textarea` = raw multi-line (rendered full width; e.g. a comma separated list). */
export interface SafetyReportParam {
  name: string;
  label: string;
  kind: SafetyReportParamKind;
  required: boolean;
  default: string | null;
  options: { value: string; label: string }[] | null;
}

export interface SafetyReportCatalogItem {
  id: string;
  title: string;
  params: SafetyReportParam[];
  available: boolean;
  reason: string | null;
}

export interface SafetyReportCatalogResponse {
  data: SafetyReportCatalogItem[];
  count: number;
}

export type SafetyReportColumnKind = 'text' | 'num' | 'dec1' | 'dec2' | 'date' | string;

export interface SafetyReportColumn {
  key: string;
  header: string;
  kind: SafetyReportColumnKind;
  width: number;
}

/** Rows are keyed by `columns[].key`; `_kind` marks total/title rows (rendered bold). */
export type SafetyReportRow = Record<string, unknown> & { _kind?: string };

export interface SafetyReportSheet {
  name: string;
  title: string | null;
  columns: SafetyReportColumn[];
  rows: SafetyReportRow[];
  count: number;
}

export interface SafetyReportRunResult {
  report_id: string;
  title: string;
  params: Record<string, string | null>;
  sheets: SafetyReportSheet[];
  notes: string[];
  /** The workbook name the xlsx route will use (`<Prefix>_<userid>_<YYYY-MM-DD>.xlsx`). */
  filename: string;
  run_at: string;
}

// ---------------------------------------------------------------------------
// Safety Tables (Phase 5) — GET /safety-system/tables… (tablemaint/registry.py)
// ---------------------------------------------------------------------------

export type SafetyTableFieldKind =
  | 'text' | 'textarea' | 'int' | 'decimal' | 'yn' | 'date' | 'datetime' | 'select' | 'lookup' | 'phone' | 'ssn';

/** `registry.Field.as_dict()` — one field of a table (or child) spec. */
export interface SafetyTableField {
  name: string;
  label: string;
  kind: SafetyTableFieldKind;
  required: boolean;
  /** False = shown read-only (isactive, createdatetime, surveyrating); never posted. */
  editable: boolean;
  /** Editable on create, read-only afterwards (survey.surveytyperecno). */
  create_only: boolean;
  in_grid: boolean;
  /** `select` choices. */
  options: string[] | null;
  /** `select`: keep an off-list stored value selectable. */
  allow_other: boolean;
  /** `lookup`: another table key or a built-in source (`states`, `contacttypes`, `counties`, `users`, `drivers`). */
  lookup: string | null;
  /** `lookup`: 0 / NULL renders blank, blank saves 0. */
  fk_zero_blank: boolean;
  /** `ssn`: masked read; reveal / edit need `safety-system-payroll-sensitive` level 2. */
  sensitive: boolean;
  width: number | null;
}

/** `registry.ChildSpec.as_dict()`. `managed: false` = the parent's hooks own the rows (survey `elements`). */
export interface SafetyTableChildSpec {
  name: string;
  title: string;
  fields: SafetyTableField[];
  /** The soft-delete column (`deleted`) or null when rows are never removed. */
  soft_delete: string | null;
  managed: boolean;
}

/** One catalog entry of GET /safety-system/tables. */
export interface SafetyTableSpec {
  key: string;
  title: string;
  /** The physical `safetynew` table (Documents `tablename`, history `dbtable`). */
  table: string;
  /** Caller's component level (0 for `external_route` entries, which carry no component). */
  level: number;
  readonly: boolean;
  no_create: boolean;
  external_route: string | null;
  documents: boolean;
  soft_delete: string | null;
  label_col: string | null;
  fields: SafetyTableField[];
  /** Grid-only computed columns (e.g. `countylist`). */
  computed: string[];
  children: SafetyTableChildSpec[];
}

export interface SafetyTableCatalogResponse {
  data: SafetyTableSpec[];
  count: number;
}

export interface SafetyLookupOption {
  value: string | number;
  label: string;
}

/** A grid / drawer row: declared columns + `<lookup>_label` keys; dates ISO, zero dates ''. */
export type SafetyTableRow = Record<string, unknown> & { recno: number };

/** Active choices per lookup field (drivers excluded) — what the drawer / child editors consume. */
export type SafetyLookupOptions = Record<string, SafetyLookupOption[]>;

/** `survey` only: one active `survey_category` row. */
export interface SafetyCategoryOption {
  surveycategoryrecno: number;
  category: string;
}

/**
 * The `options` payload of GET /tables/{key} and /tables/{key}/{recno}: lookup lists keyed by
 * field name plus spec extras (`TableSpec.extra_options`). `survey` adds `categories_by_type`
 * (`{"<surveytyperecno>": [...]}`) so a New survey never needs the `surveytype` grant.
 * `lookupOptions()` (tableForm.ts) strips the extras for consumers that want the lists only.
 */
export interface SafetyTableOptions {
  [field: string]: SafetyLookupOption[] | Record<string, SafetyCategoryOption[]> | undefined;
  categories_by_type?: Record<string, SafetyCategoryOption[]>;
}

/** GET /safety-system/tables/{key}?include_deleted=&options_only= (`rows` is `[]` with `options_only`). */
export interface SafetyTableRows {
  rows: SafetyTableRow[];
  options: SafetyTableOptions;
}

/** GET /safety-system/tables/{key}/{recno}?include_deleted= */
export interface SafetyTableRecord {
  row: SafetyTableRow;
  children: Record<string, Record<string, unknown>[]>;
  options: SafetyTableOptions;
}

/** POST / PUT body: only editable fields that changed, plus full child arrays. */
export interface SafetyTableSaveBody {
  row: Record<string, unknown>;
  children?: Record<string, Record<string, unknown>[]>;
}

export interface SafetyTableSaveResponse {
  ok?: boolean;
  recno?: number;
  changed?: string[];
  children?: Record<string, { inserted: number[]; updated: number[]; deleted: number[] }>;
}

/** One `tablemaint_history` row (same columns as the other Safety history readers). */
export type SafetyTableHistoryRow = SafetyHistoryRow;

// ---------------------------------------------------------------------------
// Deferred items (Phase 5 Task 6/7) — free_liability.py, unit_notifications.py
// ---------------------------------------------------------------------------

/** One `cms.driverpay_reimburse` FREE LIABILITY row (GET /units/{r}/free-liability), newest first. */
export interface SafetyFreeLiabilityRow {
  description: string;
  /** Two-decimal string. */
  amount: string;
  /** ISO date, '' when the row has no payment date. */
  firstpaymentdate: string;
}

/** POST /units/{r}/free-liability body. */
export interface SafetyFreeLiabilityAdd {
  /** `YYYY-MM-DD` — any day of the week; the server pays the Friday two weeks after that week's Monday. */
  week_of: string;
  amount: number;
}

export interface SafetyFreeLiabilityAddResponse {
  recno: number | null;
  /** ISO date the payment begins. */
  firstpaymentdate: string;
  glcode: string;
}

/** One e-mail logged against the unit (or its current owner) — GET /units/{r}/notifications, newest first. */
export interface SafetyUnitNotification {
  recno: number;
  /** `YYYY-MM-DD HH:MM`. */
  datetime: string;
  emailto: string;
  subject: string;
  body: string;
  tablename: string;
  tablerecno: number;
  source: 'unit' | 'owner';
}
